import * as React from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { markdownSanitizeSchema } from '~/lib/markdownSanitizeSchema';
import {
  WatchWorkflowExecutionSubscription,
  WorkflowTaskEventType,
  WorkflowTaskDefinitionKey,
  WorkflowTaskState,
  WorkflowTaskOutputType,
  WorkflowArtifactType
} from '~/api/graphql';

export type FlattenedEvent = {
  eventId: string;
  taskId: string;
  definitionKey: WorkflowTaskDefinitionKey;
  type: WorkflowTaskEventType;
  title: string;
  createdAt: Date;
  content?: string;
  progress?: number;
};

export function flattenEventsFromPayload(
  payload: WatchWorkflowExecutionSubscription['watchWorkflowExecution']
): FlattenedEvent[] {
  const out: FlattenedEvent[] = [];
  const tasks = payload.tasks ?? [];
  for (const task of tasks) {
    const taskEvents = task.events ?? [];
    for (const ev of taskEvents) {
      let content: string | undefined;
      if (ev.message?.content) content = ev.message.content;
      else if (ev.thinking?.content) content = ev.thinking.content;
      else if (ev.progress?.message) content = ev.progress.message;
      else if (ev.retrieval)
        content = `query: ${ev.retrieval.query} (${ev.retrieval.resultCount} results)`;
      else if (ev.toolCall) content = ev.toolCall.toolName;
      else if (ev.error?.message) content = ev.error.message;
      out.push({
        eventId: ev.id,
        taskId: task.id,
        definitionKey: task.definitionKey,
        type: ev.type,
        title: ev.title,
        createdAt: ev.createdAt,
        content,
        progress: ev.progress?.percent || undefined
      });
    }
  }
  return out.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
}

export function eventTypeColor(type: WorkflowTaskEventType): string {
  switch (type) {
    case WorkflowTaskEventType.Error:
      return 'text-red-600 dark:text-red-400';
    case WorkflowTaskEventType.Warning:
      return 'text-amber-600 dark:text-amber-400';
    case WorkflowTaskEventType.Thinking:
      return 'text-neutral-500 dark:text-neutral-400';
    case WorkflowTaskEventType.Progress:
      return 'text-blue-600 dark:text-blue-400';
    case WorkflowTaskEventType.Retrieval:
      return 'text-sky-600 dark:text-sky-400';
    case WorkflowTaskEventType.ToolCall:
      return 'text-violet-600 dark:text-violet-400';
    case WorkflowTaskEventType.Message:
      return 'text-neutral-800 dark:text-neutral-200';
    default:
      return 'text-neutral-700 dark:text-neutral-300';
  }
}

export function topoSortTasks<T extends { id: string; dependencies: readonly { id: string }[] }>(
  tasks: readonly T[]
): T[] {
  const idToTask = new Map(tasks.map((t) => [t.id, t]));
  const inDegree = new Map(tasks.map((t) => [t.id, t.dependencies.length]));
  const dependents = new Map<string, string[]>();
  for (const t of tasks) {
    for (const dep of t.dependencies) {
      const list = dependents.get(dep.id) ?? [];
      list.push(t.id);
      dependents.set(dep.id, list);
    }
  }

  const queue: string[] = [];
  for (const [id, deg] of inDegree) {
    if (deg === 0) queue.push(id);
  }

  const sorted: T[] = [];
  while (queue.length > 0) {
    queue.sort();
    const id = queue.shift()!;
    sorted.push(idToTask.get(id)!);
    for (const child of dependents.get(id) ?? []) {
      const deg = inDegree.get(child)! - 1;
      inDegree.set(child, deg);
      if (deg === 0) queue.push(child);
    }
  }

  for (const t of tasks) {
    if (!sorted.includes(t)) sorted.push(t);
  }

  return sorted;
}

export function computeDepthGroups<
  T extends { id: string; definitionKey: string; dependencies: readonly { id: string }[] }
>(tasks: readonly T[]): { depth: number; tasks: T[] }[] {
  const depthMap = new Map<string, number>();
  const idToTask = new Map(tasks.map((t) => [t.id, t]));

  function getDepth(taskId: string): number {
    if (depthMap.has(taskId)) return depthMap.get(taskId)!;
    const task = idToTask.get(taskId);
    if (!task || task.dependencies.length === 0) {
      depthMap.set(taskId, 0);
      return 0;
    }
    const maxDepOfDeps = Math.max(...task.dependencies.map((d) => getDepth(d.id)));
    const depth = maxDepOfDeps + 1;
    depthMap.set(taskId, depth);
    return depth;
  }

  for (const t of tasks) getDepth(t.id);

  const grouped = new Map<number, T[]>();
  for (const t of tasks) {
    const d = depthMap.get(t.id) ?? 0;
    const list = grouped.get(d) ?? [];
    list.push(t);
    grouped.set(d, list);
  }

  return Array.from(grouped.entries())
    .sort(([a], [b]) => a - b)
    .map(([depth, tasks]) => ({
      depth,
      tasks: tasks.sort((a, b) => a.definitionKey.localeCompare(b.definitionKey))
    }));
}

export type DAGEdge = { from: string; to: string };

export function computeDAGEdges<T extends { id: string; dependencies: readonly { id: string }[] }>(
  tasks: readonly T[]
): DAGEdge[] {
  const edges: DAGEdge[] = [];
  for (const task of tasks) {
    for (const dep of task.dependencies) {
      edges.push({ from: dep.id, to: task.id });
    }
  }
  return edges;
}

export function formatDefinitionKey(key: WorkflowTaskDefinitionKey | string): string {
  return key.replace(/_/g, ' ').toLowerCase();
}

export function formatDuration(
  startedAt: Date | null | undefined,
  completedAt: Date | null | undefined
): string | null {
  if (!startedAt || !completedAt) return null;
  const ms = new Date(completedAt).getTime() - new Date(startedAt).getTime();
  if (ms < 0) return null;
  if (ms < 1000) return `${ms}ms`;
  const totalSeconds = Math.round(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60) return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
}

export function formatElapsedLive(startedAt: Date | null | undefined): string | null {
  if (!startedAt) return null;
  const ms = Date.now() - new Date(startedAt).getTime();
  if (ms < 0) return null;
  const totalSeconds = Math.floor(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
}

export function taskStateIndicator(state: WorkflowTaskState): {
  color: string;
  bg: string;
  label: string;
} {
  switch (state) {
    case WorkflowTaskState.Running:
      return { color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-500', label: 'Running' };
    case WorkflowTaskState.Completed:
      return {
        color: 'text-emerald-600 dark:text-emerald-400',
        bg: 'bg-emerald-500',
        label: 'Completed'
      };
    case WorkflowTaskState.Failed:
      return { color: 'text-red-600 dark:text-red-400', bg: 'bg-red-500', label: 'Failed' };
    case WorkflowTaskState.Skipped:
      return {
        color: 'text-neutral-400 dark:text-neutral-500',
        bg: 'bg-neutral-400 dark:bg-neutral-500',
        label: 'Skipped'
      };
    case WorkflowTaskState.Pending:
    default:
      return {
        color: 'text-neutral-400 dark:text-neutral-500',
        bg: 'bg-neutral-300 dark:bg-neutral-600',
        label: 'Pending'
      };
  }
}

export type ArtifactEntry = {
  outputId: string;
  taskDefinitionKey: WorkflowTaskDefinitionKey;
  name: string;
  type: WorkflowArtifactType;
  markdownContent?: string;
  fileId?: string;
  mimeType?: string;
  url?: string;
};

export function collectArtifacts(
  payload: WatchWorkflowExecutionSubscription['watchWorkflowExecution']
): ArtifactEntry[] {
  const out: ArtifactEntry[] = [];
  for (const task of payload.tasks ?? []) {
    for (const output of task.outputs ?? []) {
      if (output.type !== WorkflowTaskOutputType.Artifact || !output.artifact) continue;
      const a = output.artifact;
      out.push({
        outputId: output.id,
        taskDefinitionKey: task.definitionKey,
        name: a.name,
        type: a.type,
        markdownContent: a.markdown?.content ?? undefined,
        fileId: a.file?.fileId ?? undefined,
        mimeType: a.file?.mimeType ?? undefined,
        url: a.file?.url ?? undefined
      });
    }
  }
  return out;
}

export const WorkflowArtifacts = (props: { artifacts: ArtifactEntry[] }) => {
  const [activeTab, setActiveTab] = React.useState(0);

  if (props.artifacts.length === 0) return null;

  const current = props.artifacts[activeTab] ?? props.artifacts[0];

  return (
    <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900/50 overflow-hidden">
      <div className="p-2 border-b border-neutral-200 dark:border-neutral-700 bg-neutral-100/80 dark:bg-neutral-800/50 flex items-center gap-2">
        <span className="text-xs text-neutral-500 dark:text-neutral-400 shrink-0">Artifacts</span>
        <div className="flex items-center gap-1 overflow-x-auto">
          {props.artifacts.map((a, i) => (
            <button
              key={a.outputId}
              onClick={() => setActiveTab(i)}
              className={`px-2 py-0.5 rounded text-xs font-mono whitespace-nowrap transition-colors ${
                i === activeTab
                  ? 'bg-neutral-200 dark:bg-neutral-700 text-neutral-800 dark:text-neutral-200'
                  : 'text-neutral-500 dark:text-neutral-400 hover:bg-neutral-150 dark:hover:bg-neutral-800'
              }`}
            >
              {a.name}
            </button>
          ))}
        </div>
      </div>
      <div className="p-4 overflow-auto max-h-[70vh]">
        {current?.markdownContent != null && (
          <div className="markdown-container">
            <Markdown
              remarkPlugins={[[remarkGfm, {}]]}
              rehypePlugins={[
                [rehypeRaw, { tagFilter: true }],
                [rehypeSanitize, markdownSanitizeSchema]
              ]}
            >
              {current.markdownContent}
            </Markdown>
          </div>
        )}
      </div>
    </div>
  );
};

export const DEFINITION_LABELS: Record<string, string> = {
  default: 'Coverage Comparison'
};

export function executionStateLabel(state: string): {
  text: string;
  className: string;
  dotClassName?: string;
} {
  switch (state) {
    case 'RUNNING':
      return {
        text: 'Running',
        className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
        dotClassName: 'bg-blue-500 animate-pulse'
      };
    case 'COMPLETED':
      return {
        text: 'Completed',
        className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
      };
    case 'FAILED':
      return {
        text: 'Failed',
        className: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
      };
    case 'CANCELLED':
      return {
        text: 'Cancelled',
        className: 'bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300'
      };
    case 'QUEUED':
    default:
      return {
        text: 'Queued',
        className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
      };
  }
}
